/**
 * Screen 8 — "Who gets pinged". The routing mode (what the visitor can do
 * next) and the destinations that receive leads. Email to the signup address
 * is one click so the screen is skippable; "Add destination"
 * (webhook) is a first-class option, not an "Advanced" afterthought.
 */
import { useState } from 'react';
import { useSession } from '../auth/session';
import { useDraft, useDraftContinue } from '../config/draft';
import { PublishBar } from '../config/PublishBar';
import { RoutingModePicker } from '../config/sections/RoutingSection';
import { RoutingActionsSection } from '../config/sections/RoutingActionsSection';
import { WithSiteDraft } from '../config/WithSiteDraft';
import { collectsContactDetails } from '../config/validate';
import { OnboardingPage } from '../setup/OnboardingPage';
import { useSites } from '../state/sites';
import { ErrorNotice, Loading, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { DestinationForm, DestinationList, SecretReveal } from './Destinations';

function Body() {
  const d = useDraft();
  const footer = useDraftContinue();
  const { api, whoami } = useSession();
  const { current } = useSites();
  const toast = useToast();
  const list = useQuery(() => api.listDestinations(), []);
  const [form, setForm] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const quickEmail = useMutation(() => api.createDestination('email', { recipients: [whoami?.email ?? ''] }));
  const collects = collectsContactDetails(d.edited);
  const items = list.data?.items ?? [];

  return (
    <OnboardingPage
      slug="routing"
      lede="Choose what a homeowner can do once they have a diagnosis, then who hears about it."
      {...footer}
    >
      <section className="panel panel-pad">
        <RoutingModePicker />
      </section>
      <details className="panel panel-pad">
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Offer more than one button (call, book, send, app)</summary>
        <div style={{ marginTop: 12 }}>
          <RoutingActionsSection />
        </div>
      </details>
      {collects ? (
        <section className="panel" aria-labelledby="dest-h">
          <div className="panel-head">
            <h2 id="dest-h">Where new leads go</h2>
            <button type="button" className="btn btn-sm" onClick={() => setForm(!form)} aria-expanded={form}>
              {form ? 'Close' : 'Add destination'}
            </button>
          </div>
          {list.loading ? <Loading /> : <DestinationList items={items} compact />}
          {items.length === 0 && whoami?.email ? (
            <div className="panel-body row-between" style={{ borderTop: '1px solid var(--line)' }}>
              {/* An offer, not a setting: nothing is set up until the button is
                  pressed. "Email every new lead to …" read as already on. */}
              <span>
                No one gets your leads yet. Email them to <strong style={{ color: 'var(--ink)' }}>{whoami.email}</strong>?
              </span>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={quickEmail.pending}
                onClick={async () => {
                  if (await quickEmail.run()) {
                    toast('Leads will be emailed to you');
                    void list.reload();
                  }
                }}
              >
                Use my email
              </button>
            </div>
          ) : null}
          <ErrorNotice error={quickEmail.error} />
          {form ? (
            <div className="panel-body" style={{ borderTop: '1px solid var(--line)' }}>
              <DestinationForm
                defaultEmail={whoami?.email}
                onCreated={(_dest, s) => {
                  setForm(false);
                  if (s) setSecret(s);
                  void list.reload();
                }}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <div className="notice info">This mode collects no contact details, so no leads are created and no destination is needed.</div>
      )}
      <PublishBar hasTestKey={Boolean(current?.test)} />
      <SecretReveal secret={secret} onDone={() => setSecret(null)} />
    </OnboardingPage>
  );
}

export function RoutingMode() {
  return (
    <WithSiteDraft>
      <Body />
    </WithSiteDraft>
  );
}
